/**
 * RegistrationFormConfigEditor — superadmin-only section rendered inside
 * SettingsTab. Lets the superadmin control which optional fields appear on
 * the public /register/{slug} form and which fields render on the LED
 * broadcast overlay. Also supports custom form fields and a global
 * "registrations open / closed" toggle.
 *
 * Persists via PATCH /api/superadmin/auctions/{auctionId}/registration-form
 */
import React, { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

// Optional fields that superadmin can toggle ON to appear on the public form.
// Order matters — this is the exact order shown in the UI.
const OPTIONAL_FIELDS = [
  { key: "father_name", label: "Father's Name" },
  { key: "dob", label: "Date of Birth" },
  { key: "whatsapp", label: "WhatsApp Number" },
  { key: "email", label: "Email" },
  { key: "address", label: "Full Address" },
  { key: "village", label: "Village" },
  { key: "tehsil", label: "Tehsil" },
  { key: "district", label: "District" },
  { key: "state", label: "State" },
  { key: "pincode", label: "Pincode" },
  { key: "bowling_style", label: "Bowling Style" },
  { key: "location", label: "Location" },
  { key: "notes", label: "Name Of Club" },
  { key: "total_matches", label: "Total Matches" },
  { key: "total_runs", label: "Total Runs" },
  { key: "total_wickets", label: "Total Wickets" },
  { key: "hundreds", label: "100s" },
  { key: "fifties", label: "50s" },
  { key: "highest_score", label: "Highest Score" },
  { key: "best_bowling", label: "Best Bowling" },
  { key: "strike_rate", label: "Strike Rate" },
  { key: "economy_rate", label: "Economy Rate" },
  { key: "batting_avg", label: "Batting Average" },
  { key: "bowling_avg", label: "Bowling Average" },
];

// Base fields that are always on the registration form. Superadmin may still
// pick which of these render on the LED overlay (photo + name always render).
const BASE_LED_FIELDS = [
  { key: "age", label: "Age" },
  { key: "role", label: "Role" },
  { key: "batting_style", label: "Batting Style" },
  { key: "category", label: "Category" },
  { key: "mobile", label: "Mobile" },
];

const FIELD_LABEL = Object.fromEntries(
  [...OPTIONAL_FIELDS, ...BASE_LED_FIELDS].map((f) => [f.key, f.label]),
);

const CUSTOM_FIELD_TYPES = ["text", "number", "textarea", "select"];

export default function RegistrationFormConfigEditor({ auction, reload }) {
  const cfg = auction?.registration_form_config || {};
  const [enabled, setEnabled] = useState(new Set(cfg.enabled_fields || []));
  const [led, setLed] = useState(new Set(cfg.led_display_fields || []));
  const [customFields, setCustomFields] = useState(cfg.custom_fields || []);
  const [regOpen, setRegOpen] = useState(auction?.registration_open !== false);
  const [saving, setSaving] = useState(false);

  const ledEligible = useMemo(() => {
    // LED-eligible = base LED fields + currently enabled optional fields.
    return [...BASE_LED_FIELDS, ...OPTIONAL_FIELDS.filter((f) => enabled.has(f.key))];
  }, [enabled]);

  // If a field gets disabled on the form but was marked for LED, drop it.
  const toggleEnabled = (key) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setLed((prevLed) => {
          const nl = new Set(prevLed);
          nl.delete(key);
          return nl;
        });
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleLed = (key) => {
    setLed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const addCustomField = () => {
    setCustomFields((prev) => [
      ...prev,
      { id: `cf_${Date.now()}`, field_name: "", field_type: "text", required: false, options: "" },
    ]);
  };
  const updateCustomField = (idx, patch) => {
    setCustomFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const removeCustomField = (idx) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = customFields
        .filter((f) => (f.field_name || "").trim())
        .map((f) => ({
          id: f.id || `cf_${Date.now()}`,
          field_name: f.field_name.trim(),
          field_type: f.field_type || "text",
          required: !!f.required,
          options: (f.options || "").trim() || null,
        }));
      await api.patch(`/superadmin/auctions/${auction.id}/registration-form`, {
        enabled_fields: [...enabled],
        led_display_fields: [...led],
        custom_fields: clean,
        registration_open: regOpen,
      });
      toast.success("Registration form updated");
      reload?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-gray-200 pt-6 mt-6" data-testid="registration-form-config-editor">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-mono text-orange-500">REGISTRATION & LED DISPLAY · Superadmin</div>
          <p className="text-xs text-gray-500 mt-1 max-w-xl">
            Pick which optional fields show on the public <span className="font-mono">/register/{auction?.slug || auction?.id}</span> form,
            and which of those (plus base fields) render on the LED broadcast overlay.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest cursor-pointer" data-testid="reg-open-toggle">
          <input
            type="checkbox"
            checked={regOpen}
            onChange={(e) => setRegOpen(e.target.checked)}
            className="w-4 h-4 accent-emerald-500"
          />
          <span className={regOpen ? "text-emerald-600" : "text-red-500"}>{regOpen ? "REGISTRATIONS OPEN" : "REGISTRATIONS CLOSED"}</span>
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Optional form fields */}
        <div className="border border-gray-200 rounded p-4 bg-white">
          <div className="label-mono mb-3">OPTIONAL FIELDS ON PUBLIC FORM</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 max-h-72 overflow-y-auto pr-2">
            {OPTIONAL_FIELDS.map((f) => (
              <label
                key={f.key}
                className="flex items-center gap-2 text-xs cursor-pointer hover:text-orange-600"
                data-testid={`opt-field-${f.key}`}
              >
                <input
                  type="checkbox"
                  checked={enabled.has(f.key)}
                  onChange={() => toggleEnabled(f.key)}
                  className="w-3.5 h-3.5 accent-orange-500"
                />
                <span className="truncate">{f.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* LED overlay fields */}
        <div className="border border-gray-200 rounded p-4 bg-white">
          <div className="label-mono mb-1">FIELDS SHOWN ON LED OVERLAY</div>
          <p className="text-[11px] text-gray-500 mb-3">Photo &amp; player name always render. Enable others below.</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 max-h-72 overflow-y-auto pr-2">
            {ledEligible.map((f) => (
              <label
                key={f.key}
                className="flex items-center gap-2 text-xs cursor-pointer hover:text-emerald-600"
                data-testid={`led-field-${f.key}`}
              >
                <input
                  type="checkbox"
                  checked={led.has(f.key)}
                  onChange={() => toggleLed(f.key)}
                  className="w-3.5 h-3.5 accent-emerald-500"
                />
                <span className="truncate">{FIELD_LABEL[f.key] || f.key}</span>
              </label>
            ))}
            {ledEligible.length === 0 && (
              <div className="text-xs text-gray-400 col-span-2">Enable optional fields on the left first.</div>
            )}
          </div>
        </div>
      </div>

      {/* Custom fields */}
      <div className="border border-gray-200 rounded p-4 bg-white mt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono">CUSTOM FIELDS</div>
          <button
            type="button"
            onClick={addCustomField}
            className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded bg-orange-600 hover:bg-orange-500 text-white flex items-center gap-1"
            data-testid="add-custom-field"
          >
            <Plus weight="bold" size={12} /> Add Field
          </button>
        </div>
        {customFields.length === 0 ? (
          <div className="text-xs text-gray-400">No custom fields. Add tournament-specific questions above.</div>
        ) : (
          <div className="space-y-2">
            {customFields.map((f, i) => (
              <div key={f.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`custom-field-${i}`}>
                <input
                  value={f.field_name}
                  onChange={(e) => updateCustomField(i, { field_name: e.target.value })}
                  placeholder="Field label (e.g. Jersey Size)"
                  className="col-span-4 bg-white border border-gray-200 rounded px-2 py-1 text-xs focus:border-orange-500 outline-none"
                />
                <select
                  value={f.field_type || "text"}
                  onChange={(e) => updateCustomField(i, { field_type: e.target.value })}
                  className="col-span-2 bg-white border border-gray-200 rounded px-2 py-1 text-xs focus:border-orange-500 outline-none"
                >
                  {CUSTOM_FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input
                  value={f.options || ""}
                  onChange={(e) => updateCustomField(i, { options: e.target.value })}
                  placeholder={f.field_type === "select" ? "Option1, Option2, Option3" : "—"}
                  disabled={f.field_type !== "select"}
                  className="col-span-4 bg-white border border-gray-200 rounded px-2 py-1 text-xs focus:border-orange-500 outline-none disabled:bg-gray-100 disabled:text-gray-400"
                />
                <label className="col-span-1 flex items-center gap-1 text-[11px] font-bold uppercase text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!f.required}
                    onChange={(e) => updateCustomField(i, { required: e.target.checked })}
                    className="w-3.5 h-3.5 accent-orange-500"
                  />
                  Req
                </label>
                <button
                  type="button"
                  onClick={() => removeCustomField(i)}
                  className="col-span-1 text-red-500 hover:text-red-700 justify-self-end"
                  data-testid={`remove-custom-field-${i}`}
                  aria-label="Remove"
                >
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="save-registration-config"
        >
          {saving ? "Saving…" : "Save Registration & LED Config"}
        </button>
      </div>
    </div>
  );
}
